import { Card, Table, Button, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import Title from "antd/lib/typography/Title";
import { Icon } from "Components";
import { MappingChange, TenantMapping } from "../Mapping";
import { confirmMapping, PropertyMerchantMapping } from "Services/Api/PciCompliance/Mapping.Service";
import { useTranslation } from "react-i18next";

interface ReviewMappingStep3Props {
  tenantInfo: TenantMapping;
  mappedchanges: MappingChange[];
  initialData: MappingChange[];
  onConfirm: (changes: number) => void;
  onBack: () => void;
  backToSettings: () => void;
  setIsSubmitting: (isSubmitting: boolean) => void;
}

export function ReviewMappingStep3({ tenantInfo, initialData, mappedchanges, onConfirm, onBack, backToSettings, setIsSubmitting, }: ReviewMappingStep3Props) {
  const { t } = useTranslation();

  const addedMappings: MappingChange[] = mappedchanges.filter(el => !!el?.id?.startsWith("00") && el.action !== "remove");
  const modifiedMappings: MappingChange[] = [];
  const removedMappings: MappingChange[] = mappedchanges.filter(a => a.action === "remove" && initialData.find(e => e.id === a.id));
  const additionalRemoveList: MappingChange[] = [];
  for (const change of mappedchanges.filter(a => a.action !== "remove" && !a?.id?.startsWith("00"))) {
    const original = initialData.find(d => d.id === change.id);
    if (
      original?.venzaProperty !== change?.venzaProperty ||
      original?.pciPortalProperty !== change?.pciPortalProperty
    ) {
      modifiedMappings.push(change);
    }
    if (
      original?.venzaProperty !== change?.venzaProperty &&
      original?.pciPortalProperty === change?.pciPortalProperty
    ) {
      additionalRemoveList.push({
        venzaPropertyId: original?.venzaPropertyId,
        venzaProperty: original?.venzaProperty,
      });
    }
  }

  const changesToApply = [...addedMappings, ...modifiedMappings, ...removedMappings];
  const columns: ColumnsType<MappingChange> = [
    {
      title: t("mapping.reviewProperty.propertyMapping.changeType"),
      dataIndex: "changeType",
      key: "changeType",
      render: (_, record) => {
        if (addedMappings.find(el => el.id === record.id)) {
          return (
            <span data-slot="badge" className="new-button">
              <Icon type="newMapping" />
              {t("mapping.reviewProperty.propertyMapping.add")}
            </span>
          );
        }
        if (modifiedMappings.find(el => el.id === record.id)) {
          return (
            <span data-slot="badge" className="new-button">
              <Icon type="newMapping" />
              {t("mapping.reviewProperty.propertyMapping.modified")}
            </span>
          );
        }
        return (
          <span data-slot="badge" className="remove-button">
            <Icon type="removedMapping" />
            {t("mapping.reviewProperty.propertyMapping.removed")}
          </span>
        );
      },
    },
    {
      title: t("mapping.reviewProperty.propertyMapping.venzaProperty"),
      dataIndex: "venzaProperty",
      key: "venzaProperty",
    },
    {
      title: t("mapping.reviewProperty.propertyMapping.pciPortalProperty"),
      dataIndex: "pciPortalProperty",
      key: "pciPortalProperty",
      render: (text) => text || <span className="italic-font">{t("mapping.reviewProperty.propertyMapping.notPresent")}</span>,
    },
  ];

  const handleConfirm = async () => {
    if (changesToApply.length === 0) {
      backToSettings();
    } else {
      setIsSubmitting(true);
      try {

        const removeList: PropertyMerchantMapping[] = changesToApply
          .filter(e => e.action === "remove")
          .map(el => ({
            property_id: el.venzaPropertyId ?? undefined,
            property_name: el.venzaProperty ?? null,
            mid: null,
          }));

        const addedList: PropertyMerchantMapping[] = changesToApply
          .filter(e => e.action !== "remove")
          .map(el => ({
            property_id: el.venzaPropertyId ?? undefined,
            property_name: el.venzaProperty ?? null,
            mid: el.pciPortalPropertyId ?? null,
          }));

        const filteredRemovedChanges: PropertyMerchantMapping[] =
          removeList.filter(
            el => !addedList.some(e => e.property_id === el.property_id)
          );

        await confirmMapping(
          tenantInfo,
          [...filteredRemovedChanges, ...addedList]
        );

        message.success(t("mapping.errors.successMapping2"));
        console.log("API response:", [...filteredRemovedChanges, ...addedList]);
        onConfirm([...filteredRemovedChanges, ...addedList].length);
      } catch (error: any) {
        console.error("Error:", error);

        if (error.status === 404) {
          const errorMessage =
            error.data?.errors?.[0] ||
            error.data?.message ||
            "Validation failed";

          message.error(errorMessage);
        } else if (error.status === 422) {
          const errorMessage =
            error.data?.errors?.[0] ||
            error.data?.message ||
            "Validation failed";

          message.error(errorMessage);
        } else {
          message.error(t("mapping.errors.somethingWrong"));
        }
      } finally {
        setIsSubmitting(false);
      }
    }

  }
  return (
<Card>
  <div className="mbx-24">
    <Title level={3} className="headerTitle">
      {t("mapping.reviewProperty.propertyMapping.confirmTitle")}
    </Title>
    <p className="headerSubtitle">
      {t("mapping.reviewProperty.propertyMapping.confirmSubtitle")}
    </p>
  </div>

  <div className="tenantInfo">
    <strong>{t("mapping.reviewProperty.propertyMapping.clientLabel")}</strong>
    <p>{tenantInfo.name}</p>
  </div>

  <div className="review-grid-button twoColumnGrid">
    <Card className="statusCard newCard" bodyStyle={{ padding: 16 }}>
      <div className="cardHeader">
        <Icon type="newMapping" />
        <p>{t("mapping.reviewProperty.propertyMapping.newMappings")}</p>
      </div>
      <p className="cardValue">
        {addedMappings.length + modifiedMappings.length}
      </p>
    </Card>

    <Card className="statusCard pendingCard" bodyStyle={{ padding: 16 }}>
      <div className="cardHeader">
        <Icon type="removedMapping" />
        <p>{t("mapping.reviewProperty.propertyMapping.removedMappings")}</p>
      </div>
      <p className="cardValue">{removedMappings.length}</p>
    </Card>
  </div>

  {changesToApply.length > 0 ? (
    <Table
      dataSource={changesToApply}
      columns={columns}
      rowKey="id"
      pagination={false}
      bordered={false}
      className="w-100"
    />
  ) : (
    <div className="noChanges">
      <Icon type="noChanges" />
      <p className="fs-1">{t("mapping.reviewProperty.propertyMapping.noChanges")}</p>
    </div>
  )}

  <div className="footerButtons">
    <Button className="backButton" onClick={onBack}>
      {t("mapping.reviewProperty.propertyMapping.back")}
    </Button>
    <Button
      className="confirmButton"
      type="primary"
      onClick={handleConfirm}
    >
      {t("mapping.reviewProperty.propertyMapping.done")}
    </Button>
  </div>
</Card>
  );

}
